import {
    Camera,
    Canvas,
    Component,
    Sprite,
    _decorator,
    RenderTexture,
    SpriteFrame,
    Color,
    Vec3,
    EventTouch,
    Node,
    Vec2,
    Rect,
    warn,
    debug,
    Mat4,
    Layers,
} from 'cc';
import { DEV } from 'cc/env';
import ScratchTarget from '../Shader/ScratchTarget';
import ScratchBlush from './ScratchBlush';

const { ccclass, property } = _decorator;
const uiLocation: Vec2 = new Vec2(0, 0);
const tempVec3: Vec3 = new Vec3(0, 0, 0);

const tempMat4A: Mat4 = new Mat4();
const tempMat4B: Mat4 = new Mat4();

enum EventType {
    SCRATCH_PROGRESS_CHANGED = 'SCRATCH_PROGRESS_CHANGED',
}

@ccclass('ScratchBoard')
export default class ScratchBoard extends Component {
    public static readonly EVENT_TYPE = EventType;

    @property({ type: Sprite, displayName: DEV && '形狀' })
    public readonly shapeSprite: Sprite = null;

    @property({ type: Node, displayName: DEV && '筆刷根節點' })
    public readonly blushRoot: Node = null;

    @property({ type: Sprite, displayName: DEV && '刮除區域除錯預覽' })
    public readonly debugScartchSprite: Sprite = null;

    @property({ type: [ScratchTarget], displayName: DEV && '刮除目標' })
    public readonly scratchTargets: ScratchTarget[] = [];

    @property
    private _blush: ScratchBlush = null;

    @property
    private _scratchableArea: Node = null;

    @property({ tooltip: DEV && '自動移動至Canvas根節點, 避免渲染受Mask影響' })
    private readonly moveToCanvasRoot: boolean = true;

    @property({ displayName: DEV && '更新進度冷卻時間', tooltip: DEV && '冷卻時間越短效能影響越嚴重(豪秒)' })
    private readonly updateProgressCooldown: number = 100;

    private _camera: Camera = null;
    private _layer: number = -1;
    private _pixelBuffer: Uint8Array = null;
    private _shapeBoundingBox: Rect = null;
    private _scratchPercentageOffset: number = 0;
    private _scratchPercentage: number = 0;
    private _scratchProgress: number = 0;
    private _canUpdateProgress: boolean = true;
    private _hasNextProgressUpdateRegistered: boolean = false;
    private _interactable: boolean = true;
    private _originParent: Node = null;

    @property({ type: ScratchBlush, displayName: DEV && '筆刷', displayOrder: 0 })
    public get blush(): ScratchBlush {
        return this._blush;
    }
    public set blush(value: ScratchBlush) {
        if (this._blush === value) {
            return;
        }
        if (this._blush) {
            this._unregisterBlush(this._blush);
        }
        this._blush = value;
        if (this._blush) {
            this._registerBlush(this._blush);
        }
    }

    @property({ type: Node, displayName: DEV && '刮除區域' })
    public get scratchable(): Node {
        return this._scratchableArea ?? this.node;
    }
    public set scratchable(value: Node) {
        if (this._scratchableArea === value) {
            return;
        }
        this._scratchableArea = value;
    }

    public get interactable(): boolean {
        return this._interactable;
    }
    public set interactable(value: boolean) {
        if (this._interactable === value) {
            return;
        }
        this._interactable = value;
    }

    public get scratchProgress(): number {
        return this._scratchProgress;
    }

    private set scratchPercentage(value: number) {
        if (this._scratchPercentage === value) {
            return;
        }
        this._scratchPercentage = value;
        this._scratchProgress = (this._scratchPercentage - this._scratchPercentageOffset) / (1 - this._scratchPercentageOffset);
        debug(`Scratch progress: ${this._scratchProgress}`);
        this.emit(ScratchBoard.EVENT_TYPE.SCRATCH_PROGRESS_CHANGED, this._scratchProgress);
    }

    public get lastBlushPosition(): Vec3 {
        if (!this.blush) {
            warn('No blush attached to the scratch board');
            return tempVec3.set(0, 0, 0);
        }
        return this.blush.lastPosition;
    }

    public onLoad(): void {
        // handle camera and layer
        if (this.getComponent(Camera)) {
            this._camera = this.getComponent(Camera);
        } else {
            this._camera = this.node.addComponent(Camera);
            this._camera.projection = Camera.ProjectionType.ORTHO;
            this._camera.clearFlags = Camera.ClearFlag.DONT_CLEAR;
            this._camera.clearColor = new Color(0, 0, 0, 0);
            this._camera.far = 0;
        }

        // to prevent camera from being culled by mask
        if (this.moveToCanvasRoot) {
            this.changeToCanvasRoot();
        }

        this._shapeBoundingBox = this.shapeSprite.node.transform.getBoundingBox();
    }

    public onEnable(): void {
        this._layer = Layers.getUnusedUserLayer();
        if (this._layer === Layers.Enum.NONE) {
            warn('No unused layer available for ScratchBoard');
            return;
        }

        // handle touch event
        this.scratchable.on(Node.EventType.TOUCH_START, this._onTouchStart, this);
        this.scratchable.on(Node.EventType.TOUCH_MOVE, this._onTouchMove, this);
        this.scratchable.on(Node.EventType.TOUCH_END, this._onTouchFinish, this);
        this.scratchable.on(Node.EventType.TOUCH_CANCEL, this._onTouchFinish, this);

        // handle camera
        this.shapeSprite.node.layer = this._layer;
        this._camera.visibility = this._layer;
    }

    public onDisable(): void {
        if (this._layer === Layers.Enum.NONE) {
            return;
        }
        // release the layer
        Layers.freeLayer(this._layer);

        // handle touch event
        this.scratchable.off(Node.EventType.TOUCH_START, this._onTouchStart, this);
        this.scratchable.off(Node.EventType.TOUCH_MOVE, this._onTouchMove, this);
        this.scratchable.off(Node.EventType.TOUCH_END, this._onTouchFinish, this);
        this.scratchable.off(Node.EventType.TOUCH_CANCEL, this._onTouchFinish, this);

        // handle camera and layer
        this.shapeSprite.node.layer = null;
        this._camera.visibility = null;
    }

    public start(): void {
        this._initRenderTexture();
        if (this.blush) {
            this._registerBlush(this.blush);
        }
        this.reset();
    }

    public changeToCanvasRoot() {
        this._originParent = this.node.parent;
        const canvas = this.node.scene.getComponentInChildren(Canvas);
        if (canvas) {
            this.node.setParent(canvas.node, true);
        }
    }

    public restoreOriginalParent() {
        this.node.setParent(this._originParent, true);
    }

    /**
     * @param target target to check if it is visible
     * @returns visible percentage
     */
    public checkIfTargetVisible(target: Node, includeChild = false): number {
        const region = includeChild ? target.transform.getVisibleBoundingBox(Mat4.identity(tempMat4A)) : target.transform.getBoundingBox();
        // transform the region to the shape space
        Mat4.multiply(tempMat4A, this.shapeSprite.node.getWorldMatrix(tempMat4B).invert(), target.parent.getWorldMatrix(tempMat4A));
        region.transformMat4(tempMat4A);
        return this._calculateRegionScratchPercentage(region);
    }

    public async reset() {
        this.blush?.draw(false);
        await this._resetScratchShape();
    }

    private _initRenderTexture() {
        const width = this.shapeSprite.node.transformWidth * this.node.worldScale.x;
        const height = this.shapeSprite.node.transformHeight * this.node.worldScale.y;
        const orthoHeight = height / 2;
        this._camera.orthoHeight = orthoHeight;

        const renderTex = new RenderTexture();
        renderTex.initialize({ width, height });
        this._pixelBuffer = new Uint8Array(width * height * 4);
        this._camera.targetTexture = renderTex;

        const spriteFrame = new SpriteFrame();
        spriteFrame.reset({});
        spriteFrame.texture = renderTex;

        this.scratchTargets.forEach((mask) => {
            mask.scratchTexture = renderTex;
        });

        if (this.debugScartchSprite) {
            this.debugScartchSprite.spriteFrame = spriteFrame;
            // @ts-expect-error to ensure define SAMPLE_FROM_RT is enabled
            this.debugScartchSprite.updateMaterial();
        }
    }

    private async _resetScratchShape() {
        await new Promise<void>((resolve) => {
            this._camera.clearColor = new Color(255, 255, 255, 255);
            this._camera.clearFlags = Camera.ClearFlag.SOLID_COLOR; // fill the whole render texture with a white color
            this.scheduleOnce(() => {
                // reset next frame
                this._camera.clearFlags = Camera.ClearFlag.DONT_CLEAR;
                // enable  to render the shape
                this.shapeSprite.enabled = true;
                this.scheduleOnce(() => {
                    this.shapeSprite.enabled = false;
                    resolve();
                });
            });
        });
        this._scratchPercentageOffset = this._calculateTotalScratchPercentage();
        this.scratchPercentage = this._scratchPercentageOffset;
    }

    private _registerBlush(blush: ScratchBlush) {
        this.blush.node.setParent(this.blushRoot, true);
        this.blush.setLayer(this._layer);
        this.blush.draw(false);
        blush.on(ScratchBlush.EVENT_TYPE.DRAW_AREA_CHANGED, this._tryToUpdateProgress, this);
    }

    private _unregisterBlush(blush: ScratchBlush) {
        this.blush.node.setParent(null);
        blush.off(ScratchBlush.EVENT_TYPE.DRAW_AREA_CHANGED, this._tryToUpdateProgress, this);
    }

    private _onTouchStart(event: EventTouch) {
        if (!this.interactable || !this.blush) {
            return;
        }
        this.blush.draw(true);
        this._handleTouchEvent(event);
    }

    private _onTouchMove(event: EventTouch) {
        if (!this.interactable || !this.blush) {
            return;
        }
        this._handleTouchEvent(event);
    }

    private _onTouchFinish() {
        if (!this.interactable || !this.blush) {
            return;
        }
        this.blush.draw(false);
    }

    private _handleTouchEvent(event: EventTouch) {
        if (!this.blush) {
            return;
        }
        event.getUILocation(uiLocation);
        uiLocation.toVec3(tempVec3);
        this.blush.node.setWorldPosition(tempVec3);
    }

    private _calculateTotalScratchPercentage(): number {
        return this._calculateRegionScratchPercentage(this._shapeBoundingBox);
    }

    private _calculateRegionScratchPercentage(region: Rect): number {
        const { width, height } = this._shapeBoundingBox;

        // check if the region is not equal to the shape bounding box
        if (region !== this._shapeBoundingBox || !region.equals(this._shapeBoundingBox)) {
            if (!region.intersects(this._shapeBoundingBox)) {
                warn('Region is not intersected with the scratch shape');
                return 0;
            }

            // trim the region to ensure not exceeding the render texture size
            region.x = Math.min(width / 2, Math.max(-width / 2, region.x));
            region.y = Math.min(height / 2, Math.max(-height / 2, region.y));
            region.width = Math.min(width - region.x, region.width);
            region.height = Math.min(height - region.y, region.height);
            if (region.width === 0 || region.height === 0) {
                warn('Intersected region size is zero');
                return 0;
            }

            // clamp the region to ensure no floating point
            region.x = Math.floor(region.x);
            region.y = Math.floor(region.y);
            region.width = Math.floor(region.width);
            region.height = Math.floor(region.height);
        }

        // calculate the scratch percentage in the region
        this._camera.targetTexture.readPixels(region.x + width / 2, region.y + height / 2, region.width, region.height, this._pixelBuffer);
        const pixelCount = region.width * region.height;
        let scratchedPixelCount = 0;
        for (let i = 0; i < pixelCount; i++) {
            if (this._pixelBuffer[i * 4] !== 255) {
                scratchedPixelCount++;
            }
        }
        return scratchedPixelCount / pixelCount;
    }

    /**
     * update scratch progress with coldown time
     * @description to reduce the frequency of updating scratch progress to avoid performance issue
     */
    private _tryToUpdateProgress() {
        if (this._canUpdateProgress) {
            this._canUpdateProgress = false;
            // update scratch percentage after rendering
            this.scheduleOnce(() => {
                this.scratchPercentage = this._calculateTotalScratchPercentage();
                this.scheduleOnce(() => {
                    this._canUpdateProgress = true;
                    // try to update scratch progress again if there is a next update registered
                    if (this._hasNextProgressUpdateRegistered) {
                        this._hasNextProgressUpdateRegistered = false;
                        this._tryToUpdateProgress();
                    }
                }, this.updateProgressCooldown / 1000);
            });
        } else {
            this._hasNextProgressUpdateRegistered = true;
        }
    }
}
